import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { PAIRS_CACHE_MS } from "../constants/cryptoSwaps.constants";
import { cryptoSwapsAccessLatch, cryptoSwapsPrincipalKey } from "./cryptoSwapsAccessLatch";

interface CacheEntry<P> {
  pairs: P[];
  fetchedAt: number;
}

interface Snapshot<P> {
  key: string;
  pairs: P[];
  hasError: boolean;
}

export interface UseOtcPairsResult<P> {
  pairs: P[];
  isLoading: boolean;
  hasError: boolean;
  /** The read succeeded and the catalog is empty. Retrying changes nothing. */
  isEmpty: boolean;
  hasAccess: boolean;
  refresh: () => void;
}

export interface OtcPairsCatalog<P> {
  usePairs: (organizationId: string | null) => UseOtcPairsResult<P>;
  invalidate: () => void;
}

/**
 * A pair catalog, fail-closed: the pairs, their decimals and their limits all come from it, so with no
 * catalog there is nothing to render a form from. One cache per catalog, keyed by the organization the
 * availability was read for; `""` is the caller's own (or global) view.
 */
export function createOtcPairsCatalog<P>(
  fetchPairs: (organizationId: string | null) => Promise<P[]>,
): OtcPairsCatalog<P> {
  const cache = new Map<string, CacheEntry<P>>();
  const inFlight = new Map<string, Promise<P[]>>();

  function ensurePairs(organizationId: string, force: boolean): Promise<P[]> {
    const cached = cache.get(organizationId);
    if (!force && cached && Date.now() - cached.fetchedAt < PAIRS_CACHE_MS)
      return Promise.resolve(cached.pairs);

    const pending = inFlight.get(organizationId);
    if (pending) return pending;

    const request = fetchPairs(organizationId || null)
      .then((pairs) => {
        cache.set(organizationId, { pairs, fetchedAt: Date.now() });
        return pairs;
      })
      .finally(() => {
        inFlight.delete(organizationId);
      });
    inFlight.set(organizationId, request);
    return request;
  }

  function usePairs(organizationId: string | null): UseOtcPairsResult<P> {
    const { user } = useAuth();
    const principal = cryptoSwapsPrincipalKey(user?.id, user?.organization_id ?? user?.organization?.id);
    const hasAccess = !cryptoSwapsAccessLatch.useDenied(principal);
    const key = organizationId ?? "";

    const [snapshot, setSnapshot] = useState<Snapshot<P> | null>(() => {
      const cached = cache.get(key);
      return cached ? { key, pairs: cached.pairs, hasError: false } : null;
    });
    const [reloadToken, setReloadToken] = useState(0);
    const servedReloadRef = useRef(0);

    useEffect(() => {
      if (!user || cryptoSwapsAccessLatch.isDenied(principal)) return;
      let cancelled = false;
      const force = reloadToken > servedReloadRef.current;
      servedReloadRef.current = reloadToken;

      ensurePairs(key, force)
        .then((pairs) => {
          if (!cancelled) setSnapshot({ key, pairs, hasError: false });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (HttpApiService.isAccessDeniedError(error)) {
            cryptoSwapsAccessLatch.latch(principal);
            return;
          }
          setSnapshot((previous) => ({
            key,
            pairs: previous?.key === key ? previous.pairs : [],
            hasError: true,
          }));
        });

      return () => {
        cancelled = true;
      };
    }, [user, principal, key, reloadToken]);

    const refresh = useCallback(() => {
      cache.delete(key);
      setReloadToken((token) => token + 1);
    }, [key]);

    // While another organization's catalog loads, the previous one stays on screen: a skeleton would unmount
    // the organization picker mid-selection. Every quote carries the organization, so the backend decides.
    const current = snapshot?.key === key ? snapshot : null;
    const shown = current ?? snapshot;

    return useMemo(
      () => ({
        pairs: shown?.pairs ?? [],
        isLoading: shown === null,
        hasError: current?.hasError ?? false,
        isEmpty: current !== null && !current.hasError && current.pairs.length === 0,
        hasAccess,
        refresh,
      }),
      [shown, current, hasAccess, refresh],
    );
  }

  return {
    usePairs,
    invalidate: () => {
      cache.clear();
      inFlight.clear();
    },
  };
}
